import Konva from "konva";
import React, {
  createContext,
  HTMLProps,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

interface SelectionContextData {
  selectedObject: Konva.Shape;
  setSelectedObject: React.Dispatch<
    React.SetStateAction<Konva.Shape | Konva.Group>
  >;

  selectedObjects: Konva.Shape[];
  setSelectedObjects: React.Dispatch<React.SetStateAction<Konva.Shape[]>>;
  multipleSelectionRefs: React.MutableRefObject<Konva.Transformer[]>;
  currentMultipleSelection: React.MutableRefObject<Konva.Transformer | null>;
  addToMultipleSelectionRef: (transformer: Konva.Transformer) => void;
  transformerSelectedObject: React.MutableRefObject<Konva.Transformer | null>;
  text: any;
  setText: any;
  textRef: React.MutableRefObject<HTMLInputElement>;
  colorText: string;
  setColorText: React.Dispatch<React.SetStateAction<string>>;
  isOpenEdit: boolean;
  setIsOpenEdit: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloseModal: () => void;
  isClickLineMultiSelection?: boolean;
  setIsClickLineMultiSelection?: React.Dispatch<React.SetStateAction<boolean>>;
  IsModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectionColor: {
    index: number;
    length: number;
  };
  setSelectionColor: React.Dispatch<
    React.SetStateAction<{
      index: number;
      length: number;
    }>
  >;
  typeChangeColor: "color" | "background" | "none";
  setTypeChangeColor: React.Dispatch<
    React.SetStateAction<"color" | "background" | "none">
  >;
}

const SelectionContext = createContext<SelectionContextData>(
  {} as SelectionContextData
);

const SelectionHookProvider: React.FC = ({ children }) => {
  const [selectedObject, setSelectedObject] = useState<Konva.Shape | null>(
    null
  );
  const [text, setText] = useState(selectedObject?.attrs?.text);
  const [selectedObjects, setSelectedObjects] = useState<Konva.Shape[]>([]);
  const [isClickLineMultiSelection, setIsClickLineMultiSelection] =
    useState(false);
  const currentMultipleSelection = useRef<Konva.Transformer | null>(null);
  const multipleSelectionRefs = useRef<Konva.Transformer[]>([]);

  const transformerSelectedObject = useRef<Konva.Transformer | null>();
  const [IsModalVisible, setIsModalVisible] = useState(false);
  const [colorText, setColorText] = useState("");

  const textRef = useRef<HTMLInputElement>(null);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [typeChangeColor, setTypeChangeColor] = useState<
    "color" | "background" | "none"
  >("none");
  const [selectionColor, setSelectionColor] = useState<{
    index: number;
    length: number;
  }>({
    index: 0,
    length: 0
  });
  const handleCloseModal = useCallback(() => {
    setIsOpenEdit(!isOpenEdit);
  }, [isOpenEdit]);

  const addToMultipleSelectionRef = useCallback(
    (transformer: Konva.Transformer) => {
      if (
        transformer &&
        !multipleSelectionRefs?.current?.includes(transformer)
      ) {
        multipleSelectionRefs?.current.push(transformer);
      }
    },
    []
  );

  return (
    <SelectionContext.Provider
      value={{
        setSelectedObject,
        selectedObject,
        multipleSelectionRefs,
        addToMultipleSelectionRef,
        currentMultipleSelection,
        transformerSelectedObject,
        text,
        setText,
        selectedObjects,
        setSelectedObjects,
        colorText,
        setColorText,
        textRef,
        isOpenEdit,
        setIsOpenEdit,
        handleCloseModal,
        IsModalVisible,
        setIsModalVisible,
        selectionColor,
        setSelectionColor,
        typeChangeColor,
        setTypeChangeColor,
        isClickLineMultiSelection,
        setIsClickLineMultiSelection
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};

// creating hook

function useSelection(): SelectionContextData {
  const context = useContext(SelectionContext);

  if (!context) {
    throw new Error("useSelection must be used with an selectionProvider");
  }

  return context;
}

export { SelectionHookProvider, useSelection };
